import { useCallback, useEffect, useState } from 'react'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Input } from '../components/ui'
import { routeFor, useFunnelConfig } from '../funnel/useFunnel'
import {
  ApiError,
  authApi,
  eventOnce,
  funnelApi,
  initialSessionId,
  reportClientError,
  setAuth,
  type AuthResponse,
} from '../lib/api'
import { useFunnelStore } from '../store/funnel'

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

/** Matches the server's check in services/waitlist.ts. Catches typos before a round-trip. */
const EMAIL_PATTERN = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[a-z]{2,}$/i

/**
 * The account gate, placed *before* the result reveal — the point of highest
 * commitment in the funnel, and the last moment we can capture a lead from
 * someone who will otherwise read the result and bounce.
 *
 * In waitlist mode the email path is unauthenticated: it posts to /lead, which
 * creates the account server-side and places them on the list. No token ever
 * enters the browser, because nothing here is for sale. The Google path still
 * does real auth, since it gets a verified identity for free.
 */
export function EmailGateScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const config = useFunnelConfig()
  const setPlacement = useFunnelStore((state) => state.setPlacement)

  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [linkSent, setLinkSent] = useState(false)

  /**
   * Say what actually went wrong. "Check the address" is only true for a
   * validation failure — telling someone to re-check a perfectly good email
   * because we rate-limited them, or because the API was unreachable, sends
   * them in circles.
   */
  const messageFor = (caught: unknown): string => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return t('gate.errorOffline')
    if (caught instanceof ApiError) {
      if (caught.status === 429) return t('gate.errorRateLimited')
      if (caught.status === 400 || caught.code === 'INVALID_EMAIL') return t('gate.invalidEmail')
      if (caught.status >= 500 || caught.status === 404) return t('gate.errorUnavailable')
    }
    // A thrown TypeError from fetch means the API never answered at all.
    if (!(caught instanceof ApiError)) return t('gate.errorUnavailable')
    return t('gate.error')
  }

  useEffect(() => {
    void eventOnce(initialSessionId, 'email_gate_viewed', { mode: config.mode }).catch(() => {})
  }, [config.mode])

  const finish = useCallback(
    (position: number | null, alreadyOnList: boolean) => {
      setPlacement({ position, alreadyOnList })
      void funnelApi.event(initialSessionId, 'claim_completed', { mode: config.mode }).catch(() => {})
      if (position !== null) {
        void funnelApi.event(initialSessionId, 'waitlist_joined', {}).catch(() => {})
      }
      navigate(routeFor('result'), { replace: true })
    },
    [config.mode, navigate, setPlacement]
  )

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault()
    const value = email.trim()
    if (!EMAIL_PATTERN.test(value)) {
      setError(t('gate.invalidEmail'))
      return
    }
    setBusy(true)
    setError('')
    try {
      if (config.mode === 'waitlist') {
        const placement = await funnelApi.lead(initialSessionId, value)
        finish(placement.position, placement.alreadyOnList)
        return
      }
      // Paid mode keeps the original passwordless flow: the purchase has to bind
      // to a real authenticated user, so the magic-link round-trip is required.
      const response = await authApi.requestLink(value, `${window.location.origin}/f/gate`)
      if (response.devToken) {
        const auth = await authApi.verify(response.devToken)
        await completeAuthenticated(auth)
        return
      }
      setLinkSent(true)
    } catch (caught) {
      reportClientError('email_gate', caught)
      setError(messageFor(caught))
    } finally {
      setBusy(false)
    }
  }

  const completeAuthenticated = useCallback(
    async (auth: AuthResponse) => {
      setAuth(auth)
      const claimed = await funnelApi.claim(initialSessionId)
      finish(claimed.waitlist?.position ?? null, claimed.waitlist?.alreadyOnList ?? false)
    },
    [finish]
  )

  const withGoogle = async (credential?: string) => {
    if (!credential) return
    setBusy(true)
    setError('')
    try {
      await completeAuthenticated(await authApi.google(credential))
    } catch (caught) {
      reportClientError('email_gate_google', caught)
      setError(messageFor(caught))
    } finally {
      setBusy(false)
    }
  }

  if (linkSent) {
    return (
      <div className="content-column text-center">
        <p className="eyebrow">{t('gate.eyebrow')}</p>
        <h1 className="value-title">{t('handoff.title')}</h1>
        <p className="body-copy">{t('handoff.email')}</p>
      </div>
    )
  }

  return (
    <div className="content-column text-center">
      <p className="eyebrow">{t('gate.eyebrow')}</p>
      <h1 className="value-title">{t('gate.title')}</h1>
      <p className="body-copy">{t('gate.body')}</p>

      {googleClientId ? (
        <GoogleOAuthProvider clientId={googleClientId}>
          <div className="google-shell">
            <GoogleLogin
              onSuccess={(response) => void withGoogle(response.credential)}
              onError={() => setError(t('gate.error'))}
              useOneTap={false}
            />
          </div>
        </GoogleOAuthProvider>
      ) : null}

      {googleClientId ? (
        <div className="divider">
          <span />
          {t('gate.or')}
          <span />
        </div>
      ) : null}

      <form onSubmit={submitEmail} className="form-stack">
        <Input
          label={t('gate.email')}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t('gate.emailPlaceholder')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        {error ? (
          <p className="error-text" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" block size="lg" variant="sunshine" loading={busy}>
          {busy ? t('gate.working') : t('gate.cta')}
        </Button>
      </form>

      <p className="fine-print">{t('gate.note')}</p>
    </div>
  )
}
